import { ImageRole, type CatalogImage, type ImageTransform } from '@numistic/contracts'
import { DataError, useImageMutations, useUploadMedia, type ImageSubject } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { Alert, Button, Select, cn } from '@numistic/ui'
import { useId, useRef, useState } from 'react'

import { useErrorText } from '../lib/errors.js'
import { CropEditor } from './crop-dialog.js'

const ROTATIONS: ImageTransform['rotate'][] = [0, 90, 180, 270]
const next = (r: ImageTransform['rotate'], step: 1 | -1) =>
  ROTATIONS[(ROTATIONS.indexOf(r) + step + 4) % 4]!

/** Роль для очередного файла: первый — аверс, второй — реверс, дальше — «другое». */
function roleFor(existing: CatalogImage[], index: number): ImageRole {
  const taken = new Set(existing.map((i) => i.role))
  const order: ImageRole[] = ['obverse', 'reverse']
  const free = order.filter((r) => !taken.has(r))
  return free[index] ?? 'other'
}

/** Фото записи каталога или экземпляра: загрузка, роли, порядок, поворот, кадрирование. */
export function ImagesManager({
  subject,
  images,
}: {
  subject: ImageSubject
  images: CatalogImage[]
}) {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const inputId = useId()
  const input = useRef<HTMLInputElement>(null)
  const upload = useUploadMedia()
  const { attach, update, remove } = useImageMutations(subject)
  const [dragging, setDragging] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [cropping, setCropping] = useState<CatalogImage | null>(null)

  const addFiles = async (files: File[]) => {
    setError(null)
    setBusy(true)
    try {
      for (const [index, file] of files.entries()) {
        const media = await upload.mutateAsync(file)
        await attach.mutateAsync({ mediaId: media.id, role: roleFor(images, index) })
      }
    } catch (e) {
      const code = e instanceof DataError ? e.code : ''
      setError(
        code === 'UNSUPPORTED_FORMAT'
          ? t('catalog.images.unsupported')
          : code === 'FILE_TOO_LARGE'
            ? t('catalog.images.tooLarge')
            : (errorText.request(e) ?? null),
      )
    } finally {
      setBusy(false)
      if (input.current) input.current.value = ''
    }
  }

  const swap = (a: CatalogImage, b: CatalogImage) => {
    update.mutate({ id: a.id, sortKey: b.sortKey })
    update.mutate({ id: b.id, sortKey: a.sortKey })
  }

  if (cropping)
    return (
      <CropEditor
        src={cropping.urls.original}
        initial={cropping.transform.crop}
        onCancel={() => setCropping(null)}
        onApply={(crop) =>
          update.mutate(
            { id: cropping.id, transform: { ...cropping.transform, crop } },
            { onSuccess: () => setCropping(null) },
          )
        }
      />
    )

  return (
    <div className="grid gap-4">
      <div
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          void addFiles([...e.dataTransfer.files])
        }}
        className={cn(
          'grid place-items-center gap-2 rounded-lg border-2 border-dashed p-6 text-center text-sm text-muted-foreground',
          dragging && 'border-primary bg-primary/5',
        )}
      >
        <span>{busy ? t('catalog.images.uploading') : t('catalog.images.drop')}</span>
        <label
          htmlFor={inputId}
          className="cursor-pointer text-primary underline underline-offset-4 hover:decoration-2"
        >
          {t('catalog.images.choose')}
        </label>
        <input
          id={inputId}
          ref={input}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/avif,image/tiff"
          className="sr-only"
          disabled={busy}
          onChange={(e) => void addFiles([...(e.target.files ?? [])])}
        />
      </div>
      {error ? <Alert variant="destructive">{error}</Alert> : null}
      {images.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('catalog.images.empty')}</p>
      ) : null}
      <ul className="grid gap-3">
        {images.map((img, i) => (
          <li
            key={img.id}
            className="flex flex-wrap items-center gap-3 rounded-lg border p-2"
            data-testid="image-row"
          >
            <img src={img.urls.thumb} alt="" className="size-20 rounded bg-muted object-contain" />
            <Select
              className="w-36"
              aria-label={t('catalog.images.role')}
              value={img.role}
              onChange={(e) => update.mutate({ id: img.id, role: e.target.value as ImageRole })}
            >
              {ImageRole.options.map((r) => (
                <option key={r} value={r}>
                  {t(`catalog.images.roles.${r}`)}
                </option>
              ))}
            </Select>
            <div className="flex flex-wrap gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={t('catalog.images.rotateLeft')}
                onClick={() =>
                  update.mutate({
                    id: img.id,
                    transform: { ...img.transform, rotate: next(img.transform.rotate, -1) },
                  })
                }
              >
                ↺
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={t('catalog.images.rotateRight')}
                onClick={() =>
                  update.mutate({
                    id: img.id,
                    transform: { ...img.transform, rotate: next(img.transform.rotate, 1) },
                  })
                }
              >
                ↻
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setCropping(img)}>
                {t('catalog.images.crop')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={i === 0}
                onClick={() => swap(img, images[i - 1]!)}
              >
                {t('catalog.images.moveUp')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={i === images.length - 1}
                onClick={() => swap(img, images[i + 1]!)}
              >
                {t('catalog.images.moveDown')}
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => remove.mutate(img.id)}>
                {t('catalog.images.remove')}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
